import { useMutation, useQueryClient, type QueryKey } from 'react-query';
import { likeLoungeMessage, type LikeLoungeMessageResponse } from '../../services/loungeService';

type PostPreviewLike = { postId?: string; isLiked?: boolean; likesCount?: number };
type InfinitePosts =
  | {
      pages: Array<{ posts: PostPreviewLike[] } & Record<string, unknown>>;
      pageParams?: unknown[];
    }
  | undefined;

type LikeVariables = { postId: string; queryKey?: QueryKey };
type LikeContext = { previousData?: InfinitePosts; queryKey?: QueryKey; postId?: string };

export function useLikeLoungeMessage() {
  const queryClient = useQueryClient();

  return useMutation<LikeLoungeMessageResponse, Error, LikeVariables, LikeContext>({
    mutationFn: ({ postId }) => likeLoungeMessage(postId),
    onMutate: async ({ postId, queryKey }) => {
      if (!postId || !queryKey) return { previousData: undefined };

      await queryClient.cancelQueries(queryKey);
      const previousData = queryClient.getQueryData<InfinitePosts>(queryKey);

      queryClient.setQueryData<InfinitePosts>(queryKey, (old) => {
        if (!old) return old;
        const targetPostId = postId;
        const updatedPages = (old.pages || []).map((page) => {
          if (!page || !('posts' in page)) return page;
          const updatedPosts = page.posts.map((p: PostPreviewLike) => {
            if (p.postId === targetPostId) {
              const nextLiked = !p.isLiked;
              const nextCount = (p.likesCount || 0) + (nextLiked ? 1 : -1);
              return { ...p, isLiked: nextLiked, likesCount: Math.max(0, nextCount) };
            }
            return p;
          });
          return { ...page, posts: updatedPosts };
        });
        return { ...old, pages: updatedPages };
      });

      return { previousData, queryKey, postId };
    },
    onError: (_error, _variables, context) => {
      if (context?.queryKey) {
        if (context.previousData) {
          queryClient.setQueryData<InfinitePosts>(context.queryKey, context.previousData);
        } else {
          queryClient.invalidateQueries(context.queryKey);
        }
      }
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.queryKey) {
        // Delay invalidation slightly to avoid flicker from eventual consistency
        setTimeout(() => {
          queryClient.invalidateQueries(['Posts']);
        }, 600);
      }
    }
  });
}
